import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { UserCheck, UserX } from "lucide-react";
import { api } from "@/lib/api";
import { DetailHeader } from "@/components/layout/detail-header";
import { useToast } from "@/contexts/toast-context";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";

interface User {
  id: string;
  name: string;
  email: string;
  state: "active" | "inactive";
  role: string;
  createdAt: string;
}

export function UsersPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [showDeactivateModal, setShowDeactivateModal] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<User[]>("/users");
      const usersData = Array.isArray(response) ? response : [];
      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
      console.error("Failed to load users:", error);
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter users based on search
  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        user.email.toLowerCase().includes(searchValue.toLowerCase()) ||
        user.role.toLowerCase().includes(searchValue.toLowerCase()) ||
        user.state.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [users, searchValue]);

  const handleToggleUserState = async (userId: string, currentState: "active" | "inactive") => {
    const user = users.find(u => u.id === userId);
    const userName = user?.name || "User";
    const newState = currentState === "active" ? "inactive" : "active";
    
    // If deactivating, show confirmation modal first
    if (newState === "inactive") {
      setShowDeactivateModal(userId);
      return;
    }

    // For activation, proceed directly (safe action)
    try {
      setIsUpdating(userId);
      
      const response = await api.patch<User>(`/users/${userId}/state`, { state: newState });
      
      // Update the user in the local state with the response data
      setUsers(users.map(user => 
        user.id === userId ? { ...user, ...response } : user
      ));
      
      // Show success toast
      const action = newState === "active" ? "activated" : "deactivated";
      showSuccess(`User ${action} successfully`, `${userName} has been ${action}`);
    } catch (error) {
      console.error("Failed to update user state:", error);
      const action = newState === "active" ? "activate" : "deactivate";
      showError(`Failed to ${action} user`, `Could not ${action} ${userName}. Please try again.`);
    } finally {
      setIsUpdating(null);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    const userName = user?.name || "User";
    
    try {
      setIsUpdating(userId);
      
      const response = await api.patch<User>(`/users/${userId}/state`, { state: "inactive" });
      
      // Update the user in the local state with the response data
      setUsers(users.map(user => 
        user.id === userId ? { ...user, ...response } : user
      ));
      
      setShowDeactivateModal(null);
      showSuccess("User deactivated successfully", `${userName} has been deactivated`);
    } catch (error) {
      console.error("Failed to deactivate user:", error);
      showError("Failed to deactivate user", `Could not deactivate ${userName}. Please try again.`);
    } finally {
      setIsUpdating(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Invalid date";
    
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString();
    } catch (error) {
      return "Invalid date";
    }
  };

  const breadcrumb = [
    { label: "Admin" },
    { label: "Users" }
  ];

  const toggleOptions = [
    { id: "logs", label: "Logs", path: "/app/admin/logs" },
    { id: "users", label: "Users", path: "/app/admin/users" }
  ];

  const columns = [
    {
      key: "name" as keyof User,
      header: "Name",
      width: 64,
      render: (user: User) => (
        <div>
          <div className="font-medium text-xs">{user.name}</div>
          <div className="text-xs text-muted-foreground">{user.email}</div>
        </div>
      )
    },
    {
      key: "role" as keyof User,
      header: "Role",
      width: 20,
      render: (user: User) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          user.role === "admin" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
        }`}>
          {user.role}
        </span>
      )
    },
    {
      key: "state" as keyof User,
      header: "Status",
      width: 20,
      render: (user: User) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          user.state === "active" 
            ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" 
            : "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300"
        }`}>
          {user.state}
        </span>
      )
    },
    {
      key: "createdAt" as keyof User,
      header: "Created",
      width: "auto",
      render: (user: User) => (
        <div className="text-xs text-muted-foreground">
          {formatDate(user.createdAt)}
        </div>
      )
    },
    {
      key: "actions",
      header: "",
      width: 32,
      className: "text-right",
      render: (user: User) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => handleToggleUserState(user.id, user.state)}
          disabled={isUpdating === user.id}
          className={`text-xs ${user.state === "active" ? 
            "text-orange-600 hover:text-orange-700 hover:bg-orange-50" :
            "text-green-600 hover:text-green-700 hover:bg-green-50"
          }`}
        >
          {user.state === "active" ? (
            <>
              <UserX className="w-3 h-3 mr-1" />
              Deactivate
            </>
          ) : (
            <>
              <UserCheck className="w-3 h-3 mr-1" />
              Activate
            </>
          )}
        </Button>
      )
    }
  ];

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
        toggleOptions={toggleOptions}
        activeToggle="users"
        onToggleChange={(toggleId) => {
          const option = toggleOptions.find(opt => opt.id === toggleId);
          if (option) navigate(option.path);
        }}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search users..."
      />
      
      <div className="flex-1 overflow-hidden">
        <Table
          data={filteredUsers}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No users found"
          fixedHeader={true}
        />
      </div>

      {/* Deactivate User Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!showDeactivateModal}
        onClose={() => setShowDeactivateModal(null)}
        onConfirm={() => showDeactivateModal && handleDeactivateUser(showDeactivateModal)}
        title="Deactivate User"
        message="Are you sure you want to deactivate this user? They will lose access to the system until reactivated."
        confirmText="Deactivate"
        type="warning"
        isLoading={isUpdating === showDeactivateModal}
      />
    </div>
  );
}
